import { useState, useEffect, useMemo, type ChangeEvent } from "react";
import Select from "react-select";
import { useDarkMode } from "../../../../context/ThemeContext";
import {
  getCustomSelectStyles,
  FilterPopover,
  DENSE_LIST,
  DENSE_ROW,
  ICON_BTN_DANGER,
  STATUS_CHIP,
} from "../../shared";
import {
  useDriversStore,
  type Driver,
} from "../../../../store/admin/driversStore";
import { useDriversList } from "../../../../features/drivers/queries";
import { useAvailableUsers } from "../../../../features/users/queries";
import { useInvalidateDomain } from "../../../../lib/invalidation";
import { SEARCH_DEBOUNCE_MS } from "../../../../hooks/useDebounce";
import { useStatusesStore } from "../../../../store/admin/statusesStore";
import CustomAlert from "../../../../components/base/CustomAlert";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import DriverCard from "./drivers/DriverCard";
import {
  DriverDetailsModal,
  EditDriverModal,
  AddDriverModal,
} from "./drivers/modals";
import useIsMonitor from "../../../../hooks/useIsMonitor";
import { useOpenChat } from "../../../../pages/support/hooks/useOpenChat";
import PaginationControls from "../../../../components/base/PaginationControls";

const ACTIVE_STATUS_ID = "R5tW0uX4yY9cF6dH2aI8";

const initialDriverFormState = {
  userId: "",
  firstName: "",
  lastName: "",
  licenseId: "",
  licenseExp: "",
  licenseExpedition: "",
  status_id: ACTIVE_STATUS_ID,
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  profileFile: "",
  companyId: "",
  groupId: "ElZ2fZD5pcMJjwKvL0uf", //Reemplazar por groupID verdadero cuando se tenga la logica
};

export default function DriversSection() {
  const { isDarkMode } = useDarkMode();
  const { isMonitor, isReadOnly } = useIsMonitor();
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [chatLoadingDriverId, setChatLoadingDriverId] = useState<string | null>(
    null,
  );
  const itemsPerPage = 6;

  const { openDirectChatWithUser, isReady: isChatReady } = useOpenChat();

  const handleStartChatWithDriver = async (driver: Driver) => {
    if (!isChatReady || chatLoadingDriverId) return;
    if (!driver.userId) {
      setAlert({
        show: true,
        title: "No se puede iniciar el chat",
        message: "Este conductor no está vinculado a un usuario de la plataforma.",
        type: "warning",
      });
      return;
    }
    setChatLoadingDriverId(driver.id);
    try {
      const result = await openDirectChatWithUser({
        uid: driver.userId,
        display_name:
          `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim() ||
          "Conductor",
      });
      if (!result) {
        setAlert({
          show: true,
          title: "Error",
          message: "No se pudo abrir el chat con el conductor.",
          type: "error",
        });
      }
    } finally {
      setChatLoadingDriverId(null);
    }
  };

  const [selectedView, setSelectedView] = useState("list");

  const createDriver = useDriversStore((state) => state.createDriver);
  const deleteDriver = useDriversStore((state) => state.deleteDriver);
  const availableUsers = useDriversStore((state) => state.availableUsers);
  const [driverToDelete, setDriverToDelete] = useState<Driver | null>(null);

  const filterParams = useMemo(() => {
    const params: { page: number; limit: number; status?: string; search?: string } = {
      page: currentPage,
      limit: itemsPerPage,
    };
    const trimmedSearch = debouncedSearchQuery.trim();
    if (trimmedSearch) params.search = trimmedSearch;
    if (selectedFilter !== "all") params.status = selectedFilter;
    return params;
  }, [currentPage, itemsPerPage, selectedFilter, debouncedSearchQuery]);

  const driversQuery = useDriversList(filterParams);
  const drivers2 = driversQuery.data?.data ?? [];
  const meta = driversQuery.data?.meta;
  const loading = driversQuery.isLoading;
  const error = driversQuery.error ? "Error al cargar conductores" : null;

  const invalidateDomain = useInvalidateDomain();
  const invalidateDrivers = () => invalidateDomain("drivers");

  const statuses = useStatusesStore((state) => state.statuses);
  const statusesLoading = useStatusesStore((state) => state.loading);
  const fetchStatuses = useStatusesStore((state) => state.fetchStatuses);

  useEffect(() => {
    if (statuses.length === 0 && !statusesLoading) {
      fetchStatuses({ limit: 50 });
    }
  }, [statuses.length, statusesLoading, fetchStatuses]);

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newDriverForm, setNewDriverForm] = useState(() => ({
    ...initialDriverFormState,
  }));
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);

  const [alert, setAlert] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  const getCurrentCompanyId = () => localStorage.getItem("companyId") || "";
  const isCompanySelected = () => Boolean(getCurrentCompanyId());

  const getMissingRequiredFields = () => {
    const missing: string[] = [];
    if (!newDriverForm.userId) missing.push("Usuario");
    if (!newDriverForm.firstName.trim()) missing.push("Nombre");
    if (!newDriverForm.lastName.trim()) missing.push("Apellido");
    if (!newDriverForm.licenseId.trim()) missing.push("Número de licencia");
    if (!newDriverForm.licenseExpedition) missing.push("Expedición de licencia");
    if (!newDriverForm.licenseExp) missing.push("Expiración de licencia");
    if (!newDriverForm.status_id) missing.push("Estado");
    return missing;
  };

  const handleInputChange = (field: string, value: string) => {
    setNewDriverForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
        setNewDriverForm((prev) => ({ ...prev, profileFile: result }));
        setSelectedImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setNewDriverForm({ ...initialDriverFormState });
    setImagePreview(null);
    setSelectedImageFile(null);
    setSelectedDriverId(null);
  };

  const formatLicenseExpValue = (
    value: Driver["license_exp"] | string | undefined,
  ) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object" && typeof value._seconds === "number") {
      return new Date(value._seconds * 1000).toISOString().split("T")[0];
    }
    return "";
  };

  const buildDriverFormData = (companyId: string) => {
    const formData = new FormData();
    formData.append("userId", newDriverForm.userId);
    formData.append("firstName", newDriverForm.firstName);
    formData.append("lastName", newDriverForm.lastName);
    formData.append("licenseId", newDriverForm.licenseId);
    if (newDriverForm.licenseExp) {
      formData.append("licenseExp", newDriverForm.licenseExp);
    }
    if (newDriverForm.licenseExpedition) {
      formData.append("licenceExpedition", newDriverForm.licenseExpedition);
    }
    formData.append("status_id", newDriverForm.status_id || ACTIVE_STATUS_ID);
    formData.append("company_id", companyId);

    const contactsEmergency =
      newDriverForm.contactName &&
      newDriverForm.contactPhone &&
      newDriverForm.contactEmail
        ? [
            {
              name: newDriverForm.contactName,
              phone: newDriverForm.contactPhone,
              email: newDriverForm.contactEmail,
            },
          ]
        : [];
    if (contactsEmergency.length > 0) {
      formData.append("contactsEmergency", JSON.stringify(contactsEmergency));
    }
    if (selectedImageFile) {
      formData.append("profileFile", selectedImageFile);
    }
    return formData;
  };

  const updateDriver = useDriversStore((state) => state.updateDriver);

  const handleOpenEditModal = (driver: Driver) => {
    const contact = driver.contactsEmergency?.[0];
    setSelectedDriverId(driver.id);
    setNewDriverForm({
      userId: driver.userId || "",
      firstName: driver.firstName || "",
      lastName: driver.lastName || "",
      licenseId: (driver as any).licenseId || driver.license_id || "",
      licenseExp: formatLicenseExpValue((driver as any).licenseExp ?? driver.license_exp),
      licenseExpedition: formatLicenseExpValue((driver as any).licenceExpedition ?? driver.license_expedition),
      status_id: (driver as any).status_id || ACTIVE_STATUS_ID,
      contactName: contact?.name || "",
      contactPhone: contact?.phone || "",
      contactEmail: contact?.email || "",
      profileFile: "",
      companyId: driver.companyId || (driver as any).company_id || getCurrentCompanyId() || "",
      groupId: "ElZ2fZD5pcMJjwKvL0uf",
    });
    setImagePreview(driver.profileImg || null);
    setSelectedImageFile(null);
    setShowEditModal(true);
  };

  const handleDeleteDriver = async () => {
    if (!driverToDelete) return;
    const target = driverToDelete;
    setDriverToDelete(null);
    const result = await deleteDriver(target.id);
    if (result.success) {
      invalidateDrivers();
      setAlert({
        show: true,
        title: "Éxito",
        message: "Conductor eliminado",
        type: "success",
      });
    } else {
      setAlert({
        show: true,
        title: "Error",
        message: result.error || "No se pudo eliminar el conductor",
        type: "error",
      });
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    resetForm();
  };

  const handleUpdateDriver = async () => {
    if (!selectedDriverId) {
      setAlert({
        show: true,
        title: "Error",
        message: "Selecciona primero un conductor",
        type: "error",
      });
      return;
    }

    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      setAlert({
        show: true,
        title: "Campos requeridos",
        message: `Por favor complete los campos requeridos: ${missing.join(", ")}`,
        type: "error",
      });
      return;
    }

    const companyId = getCurrentCompanyId();
    if (!companyId) {
      setAlert({
        show: true,
        title: "Error",
        message: "Selecciona una compañía antes de actualizar un conductor",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = buildDriverFormData(companyId);
      const result = await updateDriver(selectedDriverId, formData);
      if (result.success) {
        setAlert({
          show: true,
          title: "Éxito",
          message: "Información actualizada",
          type: "success",
        });
        handleCloseEditModal();
        invalidateDrivers();
      } else {
        throw new Error(result.error || "Error al actualizar el conductor");
      }
    } catch (err: any) {
      setAlert({
        show: true,
        title: "Error",
        message: err.message || "Error al actualizar el conductor",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddDriver = async () => {
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      setAlert({
        show: true,
        title: "Campos requeridos",
        message: `Por favor complete los campos requeridos: ${missing.join(", ")}`,
        type: "error",
      });
      return;
    }

    const companyId = getCurrentCompanyId();
    if (!companyId) {
      setAlert({
        show: true,
        title: "Error",
        message: "Selecciona una compañía antes de agregar un conductor",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = buildDriverFormData(companyId);
      const result = await createDriver(formData);
      if (result.success) {
        setAlert({
          show: true,
          title: "Éxito",
          message: "Conductor creado exitosamente",
          type: "success",
        });
        handleCloseAddModal();
        invalidateDrivers();
      } else {
        throw new Error(result.error || "Error al crear el conductor");
      }
    } catch (err: any) {
      setAlert({
        show: true,
        title: "Error",
        message: err.message || "Error al crear el conductor",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useAvailableUsers({
    currentDriverId: showEditModal ? selectedDriverId ?? undefined : undefined,
    enabled: showAddModal || showEditModal,
  });

  const totalPages = meta?.totalPages || 1;

  const formatDate = (
    value:
      | { _seconds: number; _nanoseconds: number }
      | string
      | undefined
      | null,
  ) => {
    if (!value) return "N/A";
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) return value;
      return parsed.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    }
    if (!value._seconds) return "N/A";
    const date = new Date(value._seconds * 1000);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
      case "R5tW0uX4yY9cF6dH2aI8":
        return "text-primary-500 bg-primary-500/10";
      case "inactive":
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return "text-red-500 bg-red-500/10";
      case "on_trip":
      case "X1zC6aD0eE5iL2jN8gO4":
        return "text-blue-500 bg-blue-500/10";
      case "suspended":
      case "Y2aD7bE1fF6jM3kO9hP5":
        return "text-amber-500 bg-amber-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const hasProfileImg = (profileImg: string | undefined) => {
    return profileImg && profileImg.trim() !== "";
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
      case "R5tW0uX4yY9cF6dH2aI8":
        return "Disponible";
      case "inactive":
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return "Inactivo";
      case "on_trip":
      case "X1zC6aD0eE5iL2jN8gO4":
        return "En viaje";
      case "suspended":
      case "Y2aD7bE1fF6jM3kO9hP5":
        return "Suspendido";
      default:
        return "Desconocido";
    }
  };

  const filterOptions = [
    { value: "all", label: "Todos los conductores" },
    { value: "active", label: "Disponibles" },
    { value: "on_trip", label: "En viaje" },
    { value: "inactive", label: "Inactivos" },
    { value: "suspended", label: "Suspendidos" },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div
        className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-3 sm:px-6 sm:py-4`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
          <div className="min-w-0">
            <h1
              className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
            >
              Gestión de Conductores
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              Administra todos los conductores de la flota
            </p>
          </div>
          {!isMonitor && !isReadOnly && (
            <CRUDTooltip className="self-start sm:self-auto flex-shrink-0">
              <button
                onClick={() => setShowAddModal(true)}
                disabled={!isCompanySelected()}
                className={`text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base w-full sm:w-auto ${
                  isCompanySelected()
                    ? "bg-primary-500 hover:bg-primary-400"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                <i className="ri-add-line"></i>
                <span className="hidden sm:inline">Nuevo Conductor</span>
                <span className="sm:hidden">Nuevo</span>
              </button>
            </CRUDTooltip>
          )}
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
            <i
              className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
            ></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o licencia..."
              className={`h-9 pl-10 pr-3 py-2 ${
                isDarkMode ? "bg-dark-card" : "bg-gray-50"
              } border ${themeClasses.cardBorder} rounded-lg ${
                themeClasses.text
              } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full`}
            />
          </div>

          <FilterPopover
            isDarkMode={isDarkMode}
            activeCount={selectedFilter !== "all" ? 1 : 0}
            onClear={() => setSelectedFilter("all")}
          >
            <Select
              options={filterOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder="Todos los conductores"
              value={
                filterOptions.find((opt) => opt.value === selectedFilter) ||
                null
              }
              onChange={(option: any) =>
                setSelectedFilter(option?.value || "all")
              }
              isSearchable
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FilterPopover>

          {/* View Toggle */}
          <div className="ml-auto flex shrink-0">
            <div
              className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
            >
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "grid"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("grid")}
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "list"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("list")}
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        {loading ? (
          <div className="text-center text-gray-500">
            Cargando conductores...
          </div>
        ) : error || drivers2.length === 0 ? (
          <div className="text-center text-gray-500">
            {error || "No existen conductores que coincidan con el filtro"}
          </div>
        ) : selectedView === "grid" ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {drivers2.map((driver) => (
                <DriverCard
                  key={driver.id}
                  driver={driver}
                  themeClasses={themeClasses}
                  getStatusColor={getStatusColor}
                  getStatusText={getStatusText}
                  handleOpenEditModal={handleOpenEditModal}
                  setSelectedDriver={setSelectedDriver}
                  setShowDetailModal={setShowDetailModal}
                  onStartChat={handleStartChatWithDriver}
                  isStartingChat={chatLoadingDriverId === driver.id}
                  isCompanySelected={isCompanySelected()}
                  onDelete={
                    !isMonitor && !isReadOnly
                      ? () => setDriverToDelete(driver)
                      : undefined
                  }
                />
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        ) : selectedView === "list" ? (
          <>
            <div className={DENSE_LIST}>
              {drivers2.map((driver) => {
                const statusKey = (driver as any).status_id || driver.driverStatus;
                const fullName = `${driver.firstName} ${driver.lastName}`;
                return (
                <div
                  key={driver.id}
                  className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
                >
                  {hasProfileImg(driver.profileImg) ? (
                    <img
                      src={driver.profileImg}
                      alt={driver.firstName}
                      className={`w-9 h-9 rounded-lg object-cover border flex-shrink-0 ${
                        isDarkMode ? "border-dark-border" : "border-gray-200"
                      }`}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary-500 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-900">
                        {getInitials(driver.firstName, driver.lastName)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={fullName}>
                        {fullName}
                      </h3>
                      <span className={`${STATUS_CHIP} ${getStatusColor(statusKey)}`}>
                        {getStatusText(statusKey)}
                      </span>
                    </div>
                    <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
                      {driver.license_id}
                    </p>
                  </div>
                  {/* Antes había estrellas (3.2), "6 años" y "100 viajes" fijos en
                      el código, iguales para todos los conductores: no se muestran. */}
                  <div
                    className="hidden md:block w-40 shrink-0 min-w-0"
                    title={`Vehículo: ${driver.vehicle_id || "No disponible"}`}
                  >
                    <p className={`text-[11px] ${themeClasses.textSecondary}`}>Vehículo</p>
                    <p className={`text-sm ${themeClasses.text} truncate`}>
                      {driver.vehicle_id || "No disponible"}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {!isMonitor && !isReadOnly && (
                      <button
                        onClick={() => setDriverToDelete(driver)}
                        className={ICON_BTN_DANGER}
                        aria-label="Eliminar"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            {/* Paginación */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        ) : (
          <>
            <span>No hay datos diponibles</span>
          </>
        )}
      </div>

      {/* Add Driver Modal */}
      {showAddModal && (
        <AddDriverModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          driverForm={newDriverForm}
          availableUsers={availableUsers}
          imagePreview={imagePreview}
          isSubmitting={isSubmitting}
          statuses={statuses}
          statusesLoading={statusesLoading}
          onInputChange={handleInputChange}
          onUserSelect={(userId) => {
            const selectedUser = availableUsers.find((u) => u.id === userId);
            if (selectedUser) {
              setNewDriverForm((prev) => ({
                ...prev,
                userId: selectedUser.id,
                firstName: selectedUser.firstName || "",
                lastName: selectedUser.lastName || "",
              }));
            } else {
              handleInputChange("userId", userId);
            }
          }}
          onImageChange={handleImageChange}
          onClose={handleCloseAddModal}
          onSubmit={handleAddDriver}
        />
      )}

      {showEditModal && (
        <EditDriverModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          driverForm={newDriverForm}
          availableUsers={availableUsers}
          imagePreview={imagePreview}
          isSubmitting={isSubmitting}
          statuses={statuses}
          statusesLoading={statusesLoading}
          onInputChange={handleInputChange}
          onUserSelect={(userId) => {
            const selectedUser = availableUsers.find((u) => u.id === userId);
            if (selectedUser) {
              setNewDriverForm((prev) => ({
                ...prev,
                userId: selectedUser.id,
                firstName: selectedUser.firstName || "",
                lastName: selectedUser.lastName || "",
              }));
            } else {
              handleInputChange("userId", userId);
            }
          }}
          onImageChange={handleImageChange}
          onClose={handleCloseEditModal}
          onSubmit={handleUpdateDriver}
        />
      )}

      {/* Driver Detail Modal */}
      {showDetailModal && selectedDriver && (
        <DriverDetailsModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          driver={selectedDriver}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          formatDate={formatDate}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedDriver(null);
          }}
          onEdit={() => {
            setShowDetailModal(false);
            handleOpenEditModal(selectedDriver);
          }}
        />
      )}
      {alert.show && (
        <CustomAlert
          title={alert.title}
          text={alert.message}
          icon={alert.type as any}
          onConfirm={() => setAlert((prev) => ({ ...prev, show: false }))}
        />
      )}

      {driverToDelete && (
        <CustomAlert
          title="¿Eliminar conductor?"
          text={`Esta acción marcará a ${driverToDelete.firstName ?? ""} ${
            driverToDelete.lastName ?? ""
          }`.trim() + " como eliminado."}
          icon="warning"
          showCancelButton
          confirmButtonText="Eliminar"
          cancelButtonText="Cancelar"
          onConfirm={handleDeleteDriver}
          onCancel={() => setDriverToDelete(null)}
        />
      )}
    </div>
  );
}
